import { useCallback, useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useAppSelector } from "@/app/hooks";
import type { RootState } from "@/app/store";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import { useCreateConversationMutation } from "./conversationApi";
import {
  conversationCreateDraftFingerprint,
  readConversationCreateOutbox,
  resolveConversationCreateOutboxReuse,
  writeConversationCreateOutbox,
} from "./conversationCreateOutbox";
import { resolveConversationShopBindingId } from "./conversationShopScope";
import type { ChatAddMenuSendContext } from "./ChatAddMenuComposer";
import type { PendingHomeIntent } from "./pendingHomeIntent";
import { readPendingHomeIntent, writePendingHomeIntent } from "./pendingHomeIntent";
import { createWorkspaceLaunchScopeGuard, WorkspaceLaunchScopeChangedError } from "./workspaceLaunchScope";

export interface CreateWorkspaceConversationLaunchOptions {
  pending?: PendingHomeIntent;
  preferredShopBindingId?: string | null;
}

export function useCreateWorkspaceConversation(canSendAI: boolean, enabled: boolean) {
  const actor = useAppSelector(selectAuthActor);
  const authStore = useStore<RootState>();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const captureScope = useCallback(() => {
    const guard = createWorkspaceLaunchScopeGuard(actor, () => selectAuthActor(authStore.getState()), authStore.subscribe);
    // StrictMode 会在开发态执行一次 effect setup→cleanup→setup。范围 guard 必须读取
    // 当前挂载状态，不能捕获第一次 setup 的对象，否则正常创建回执会被误判为已卸载。
    const isCurrent = () => active.current && guard.isCurrent();
    return { dispose: guard.dispose, isCurrent, assertCurrent: () => { if (!isCurrent()) throw new WorkspaceLaunchScopeChangedError(); } };
  }, [actor, authStore]);
  const userId = actor?.userId ?? "";
  const organizationId = actor?.activeOrganizationId ?? "";
  const selected = useAppSelector(state => selectWorkspaceStoreSeedBindingId(state, organizationId));
  const stores = useGetMerchantStoreBindingsQuery(organizationId, { skip: !organizationId || !enabled });
  const [create, creation] = useCreateConversationMutation();
  const ready = Boolean(enabled && actor && stores.currentData && !stores.isFetching && !stores.isError);
  const launch = useCallback(async (text: string, sendContext: ChatAddMenuSendContext, options: CreateWorkspaceConversationLaunchOptions = {}) => {
    const pending = options.pending;
    const guard = captureScope();
    const assertCurrent = () => {
      guard.assertCurrent();
      if (pending && readPendingHomeIntent()?.id !== pending.id) throw new WorkspaceLaunchScopeChangedError();
    };
    try {
      assertCurrent();
      if (!organizationId || !userId) throw new Error("当前登录状态无效，请重新登录");
      if (!canSendAI) throw new Error("AI 使用额度已用完，请充值后重试");
      const draftFingerprint = conversationCreateDraftFingerprint(text, sendContext);
      const reused = resolveConversationCreateOutboxReuse(
        readConversationCreateOutbox(userId, organizationId),
        {
          draftFingerprint,
          organizationId,
          pendingConversationId: pending?.conversationId,
          userId,
        },
      );
      const resolveCurrentShop = () => {
        if (stores.isError) throw new Error("店铺上下文读取失败，请重新读取后再发送");
        if (!ready) throw new Error("正在读取店铺上下文，请稍后重试");
        const resolved = resolveConversationShopBindingId(
          selected,
          stores.currentData?.bindings ?? [],
          options.preferredShopBindingId,
        );
        if (resolved === null) throw new Error("请先在顶部店铺菜单中选择本次对话使用的店铺");
        return resolved;
      };
      let conversationId = pending?.conversationId?.trim() || reused?.conversationId || "";
      let shopBindingId = reused?.shopBindingId;
      if (!conversationId) {
        shopBindingId = resolveCurrentShop();
        const normalized = text.replace(/\s+/g, " ").trim();
        const created = await create({
          initialResourceIds: sendContext.resourceIds,
          organizationId,
          shopBindingId,
          userId,
          title: normalized ? (normalized.length > 32 ? `${normalized.slice(0, 32)}…` : normalized) : "新对话",
        }).unwrap();
        conversationId = created.conversation.id;
      } else if (shopBindingId === undefined) {
        shopBindingId = resolveCurrentShop();
      }
      if (!conversationId || shopBindingId === undefined) {
        throw new Error("新建对话失败，请稍后重试");
      }
      // 201 或已有 ID 必须在任何后续 assertCurrent 之前落盘；手输与 pending 共用同一 outbox。
      writeConversationCreateOutbox({
        schemaVersion: 1,
        conversationId,
        draftFingerprint,
        organizationId,
        shopBindingId,
        userId,
      });
      if (pending) {
        try {
          const current = readPendingHomeIntent();
          if (current?.id === pending.id) {
            writePendingHomeIntent({ ...current, conversationId, userId, organizationId });
          }
        } catch {
          /* outbox 已有会话 ID，pending 写入失败不能把创建成功说成未创建。 */
        }
      }
      assertCurrent();
      return { conversationId, shopBindingId };
    } finally { guard.dispose(); }
  }, [canSendAI, captureScope, create, organizationId, ready, selected, stores.currentData, stores.isError, userId]);
  return { launch, captureScope, ready, isCreating: creation.isLoading, failed: stores.isError, retry: () => { if (!stores.isUninitialized) void stores.refetch(); } };
}
